import { Grid, GridConfig, DEFAULT_GRID_CONFIG } from './Grid.js';
import { Position, manhattanDistance, positionKey } from './GridGeometry.js';
import { GameType } from '../persistence/types.js';
import { ActiveRules, activeRules } from './generationRules.js';

const MIN_DIGIT_DISTANCE = 3;
const MAX_DIGITS_PER_ROW_COL = 2;
const MAX_DIGITS_ON_EDGE = 1;
const MAX_DIGITS_NEAR_CORNER = 1;
const CORNER_PROXIMITY_DISTANCE = 3;
const MAX_DIGITS_PER_QUARTER_POS = 2;

/**
 * Would placing a digit at `pos` put it on the opposite edge from an already-placed digit,
 * on the same line? The grid wraps, so such a pair sits adjacent across the seam, boxing a
 * digit in where a Crossing ball can never reach it.
 *
 * Both axes are handled: left/right edges sharing a row, and top/bottom edges sharing a
 * column. The column half only matters once the top-row rule is relaxed (a set without
 * Flood/Deluge) — until then no digit can reach the top edge. See "Rule Ownership" in
 * docs/generation.md.
 *
 * Exported so the column half can be tested directly.
 */
export function hasOppositeEdgeWrapConflict(
    pos: Position,
    placed: Iterable<Position>,
    size: number
): boolean {
    const last = size - 1;
    for (const other of placed) {
        const horizontal = other.row === pos.row
            && ((pos.col === 0 && other.col === last) || (pos.col === last && other.col === 0));
        const vertical = other.col === pos.col
            && ((pos.row === 0 && other.row === last) || (pos.row === last && other.row === 0));
        if (horizontal || vertical) return true;
    }
    return false;
}

export class GridGenerator {
    private forbiddenPositions: Set<string> = new Set();
    private readonly config: GridConfig;
    private readonly rules: ActiveRules;

    /**
     * @param games The set the grid is generated for; optional rules are enforced only for
     *   games in it (see {@link activeRules}). Omit to enforce **all** optional rules — a
     *   grid valid for every game, which is what the plain cli/test callers want.
     */
    constructor(config: GridConfig = DEFAULT_GRID_CONFIG, games?: Iterable<GameType>) {
        this.config = config;
        this.rules = activeRules(games);
        this.buildForbiddenPositions();
    }

    private buildForbiddenPositions(): void {
        const size = this.config.size;

        // Rule 1: No digit in top row (Flood/Deluge)
        if (this.rules.topRow) {
            for (let col = 0; col < size; col++) {
                this.forbiddenPositions.add(positionKey({ row: 0, col }));
            }
        }

        // Rule 2: No digit in corner 2x2 squares
        const cornerOffset = size - 2;
        const corners = [
            { rowStart: 0, colStart: 0 },                    // top-left
            { rowStart: 0, colStart: cornerOffset },         // top-right
            { rowStart: cornerOffset, colStart: 0 },         // bottom-left
            { rowStart: cornerOffset, colStart: cornerOffset } // bottom-right
        ];
        for (const corner of corners) {
            for (let r = 0; r < 2; r++) {
                for (let c = 0; c < 2; c++) {
                    this.forbiddenPositions.add(
                        positionKey({ row: corner.rowStart + r, col: corner.colStart + c })
                    );
                }
            }
        }

        // Rule 3: No digit in center 2x2 square (Orbits/Ignition)
        if (this.rules.centerSquare) {
            const centerStart = Math.floor(size / 2) - 1;
            for (let r = centerStart; r <= centerStart + 1; r++) {
                for (let c = centerStart; c <= centerStart + 1; c++) {
                    this.forbiddenPositions.add(positionKey({ row: r, col: c }));
                }
            }
        }
    }

    private isOnEdge(pos: Position): 'bottom' | 'left' | 'right' | null {
        if (pos.row === this.config.size - 1) return 'bottom';
        if (pos.col === 0) return 'left';
        if (pos.col === this.config.size - 1) return 'right';
        return null;
    }

    /**
     * Where a cell sits *inside* its quarter — the four cells sharing a key are the same
     * spot in NW, NE, SW and SE. Null on an odd-sized grid, which has no quarters.
     *
     * This is the unit Jumps works in: a jump translates by half the grid, so it preserves
     * quarter-position. See {@link MAX_DIGITS_PER_QUARTER_POS}.
     */
    private quarterPosKey(pos: Position): string | null {
        const size = this.config.size;
        if (size % 2 !== 0) return null;
        const half = size / 2;
        return `${pos.row % half},${pos.col % half}`;
    }

    private getNearCorners(pos: Position): string[] {
        const size = this.config.size;
        const corners: Array<{ name: string; position: Position }> = [
            { name: 'topLeft', position: { row: 0, col: 0 } },
            { name: 'topRight', position: { row: 0, col: size - 1 } },
            { name: 'bottomLeft', position: { row: size - 1, col: 0 } },
            { name: 'bottomRight', position: { row: size - 1, col: size - 1 } }
        ];

        return corners
            .filter(c => manhattanDistance(pos, c.position) <= CORNER_PROXIMITY_DISTANCE)
            .map(c => c.name);
    }

    private getValidPositions(
        currentDigit: number,
        placedDigits: Map<number, Position>,
        rowCounts: Map<number, number>,
        colCounts: Map<number, number>,
        edgeCounts: Map<string, number>,
        cornerCounts: Map<string, number>,
        quarterPosCounts: Map<string, number>
    ): Position[] {
        const valid: Position[] = [];
        const size = this.config.size;

        for (let row = 0; row < size; row++) {
            for (let col = 0; col < size; col++) {
                const pos: Position = { row, col };
                const key = positionKey(pos);

                // Check forbidden positions
                if (this.forbiddenPositions.has(key)) continue;

                // Check already occupied
                let occupied = false;
                for (const placed of placedDigits.values()) {
                    if (placed.row === row && placed.col === col) {
                        occupied = true;
                        break;
                    }
                }
                if (occupied) continue;

                // Check minimum distance from all placed digits
                let tooClose = false;
                for (const placed of placedDigits.values()) {
                    if (manhattanDistance(pos, placed) < MIN_DIGIT_DISTANCE) {
                        tooClose = true;
                        break;
                    }
                }
                if (tooClose) continue;

                // Check row/column limits
                const edge = this.isOnEdge(pos);
                if (edge) {
                    // Edge lines (bottom/left/right): at most 1 digit
                    if ((edgeCounts.get(edge) ?? 0) >= MAX_DIGITS_ON_EDGE) continue;
                }

                // No digits on opposite edges of the same line (Crossing) — the wrap would
                // place them adjacent, blocking a ball from ever reaching the boxed-in digit.
                if (this.rules.oppositeEdge
                    && hasOppositeEdgeWrapConflict(pos, placedDigits.values(), size)) continue;

                // At most 2 digits per row/column — counting edge digits too, so
                // an interior row can't reach 3 via a digit in an edge column.
                if ((rowCounts.get(row) ?? 0) >= MAX_DIGITS_PER_ROW_COL) continue;
                if ((colCounts.get(col) ?? 0) >= MAX_DIGITS_PER_ROW_COL) continue;

                // At most 2 digits sharing a quarter-position (Jumps), or the grid is
                // unsolvable. A jump translates by half the grid, so it preserves
                // quarter-position: to land on a cell at quarter-position p the launcher
                // must itself be at p. Only four cells share a p — one per quarter — and a
                // digit cell is a landing only, never a launcher. Three digits at p would
                // therefore need three distinct launchers from the one remaining non-digit
                // cell. (Two is always fine: every pair of quarters matches to the other two.)
                if (this.rules.quarterPosition) {
                    const qpKey = this.quarterPosKey(pos);
                    if (qpKey && (quarterPosCounts.get(qpKey) ?? 0) >= MAX_DIGITS_PER_QUARTER_POS) {
                        continue;
                    }
                }

                // Check corner proximity limits (relaxed for Vortex grids)
                if (!this.config.isVortex) {
                    const nearCorners = this.getNearCorners(pos);
                    const cornerLimitExceeded = nearCorners.some(
                        corner => (cornerCounts.get(corner) ?? 0) >= MAX_DIGITS_NEAR_CORNER
                    );
                    if (cornerLimitExceeded) continue;
                }

                // Check no consecutive digits in same row/column (cyclic: max and min are consecutive)
                const prevDigit = currentDigit > this.config.min ? currentDigit - 1 : null;
                const nextDigit = currentDigit === this.config.max ? this.config.min : null;

                let consecutiveConflict = false;
                if (prevDigit) {
                    const prevPos = placedDigits.get(prevDigit);
                    if (prevPos && (prevPos.row === row || prevPos.col === col)) {
                        consecutiveConflict = true;
                    }
                }
                if (!consecutiveConflict && nextDigit) {
                    const nextPos = placedDigits.get(nextDigit);
                    if (nextPos && (nextPos.row === row || nextPos.col === col)) {
                        consecutiveConflict = true;
                    }
                }
                if (consecutiveConflict) continue;

                valid.push(pos);
            }
        }

        return valid;
    }

    generate(): Grid {
        const maxAttempts = 100;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const result = this.tryGenerate();
            if (result) return result;
        }

        throw new Error('Failed to generate valid grid after maximum attempts');
    }

    private tryGenerate(): Grid | null {
        const grid = new Grid(this.config);
        const placedDigits = new Map<number, Position>();
        const rowCounts = new Map<number, number>();
        const colCounts = new Map<number, number>();
        const edgeCounts = new Map<string, number>();
        const cornerCounts = new Map<string, number>();
        const quarterPosCounts = new Map<string, number>();

        for (let digit = this.config.min; digit <= this.config.max; digit++) {
            const validPositions = this.getValidPositions(
                digit, placedDigits, rowCounts, colCounts, edgeCounts, cornerCounts,
                quarterPosCounts
            );

            if (validPositions.length === 0) {
                return null; // Failed, need to restart
            }

            // Random selection
            const pos = validPositions[Math.floor(Math.random() * validPositions.length)];

            // Place digit
            grid.setDigit(pos, digit);
            placedDigits.set(digit, pos);

            // Update counts
            const edge = this.isOnEdge(pos);
            if (edge) {
                edgeCounts.set(edge, (edgeCounts.get(edge) ?? 0) + 1);
            }
            rowCounts.set(pos.row, (rowCounts.get(pos.row) ?? 0) + 1);
            colCounts.set(pos.col, (colCounts.get(pos.col) ?? 0) + 1);

            for (const corner of this.getNearCorners(pos)) {
                cornerCounts.set(corner, (cornerCounts.get(corner) ?? 0) + 1);
            }

            const qpKey = this.quarterPosKey(pos);
            if (qpKey) quarterPosCounts.set(qpKey, (quarterPosCounts.get(qpKey) ?? 0) + 1);
        }

        // Reject grids where digit rows are in strictly decreasing order
        // (bottom-to-top increasing digits = trivially solvable Flood/Deluge)
        if (this.rules.noStrictDecreasingRows && this.isStrictlyDecreasingRows(placedDigits)) {
            return null;
        }

        return grid;
    }

    private isStrictlyDecreasingRows(placedDigits: Map<number, Position>): boolean {
        for (let d = this.config.min; d < this.config.max; d++) {
            const pos1 = placedDigits.get(d)!;
            const pos2 = placedDigits.get(d + 1)!;
            if (pos1.row <= pos2.row) return false;
        }
        return true;
    }

    generateEasier(): Grid {
        // Generate grids where consecutive digits are closer together
        const maxAttempts = 200;
        let bestGrid: Grid | null = null;
        let bestScore = Infinity;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            const grid = this.tryGenerate();
            if (grid) {
                const score = this.calculateDifficultyScore(grid);
                if (score < bestScore) {
                    bestScore = score;
                    bestGrid = grid;
                }
            }
        }

        if (!bestGrid) {
            throw new Error('Failed to generate valid grid');
        }

        return bestGrid;
    }

    private calculateDifficultyScore(grid: Grid): number {
        let totalDistance = 0;
        for (let d = this.config.min; d < this.config.max; d++) {
            const pos1 = grid.getDigitPosition(d);
            const pos2 = grid.getDigitPosition(d + 1);
            if (pos1 && pos2) {
                totalDistance += manhattanDistance(pos1, pos2);
            }
        }
        return totalDistance;
    }
}
